import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { GridRenderCellParams, GridColDef } from '@mui/x-data-grid-premium';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import { FakeButtonLink } from '../../components/FakeLink';
import { Table } from '../../components/Table';
import { TableRef } from '../../components/Table/Table';
import { fetchStatuses } from '../../constants/fetchStatuses';
import { userFeeDeductionStatusToInfo } from '../../constants/tables/userFeeDeduction';
import { fetchUserFeeDeductionsByBatchId } from '../../redux/modules/feeBatch/feeBatch.actions';
import { feeBatchSelector, viewDetailsByBatchIdSelector } from '../../redux/modules/feeBatch/feeBatch.selectors';
import { locations } from '../../routes/locations';
import { UserFeeDeduction, UserFeeDeductionStatus } from '../../types/userFeeDeduction';
import { round2Format } from '../../utils/number';

interface DeductionsTabProps {
  batchId: string;
}

export function DeductionsTab({ batchId }: DeductionsTabProps) {
  const tableRef = useRef<TableRef>(null);
  const [columns, setColumns] = useState<GridColDef[]>([
    {
      field: 'id',
      headerName: 'Id',
      width: 100,
      aggregable: false,
    },
    {
      field: 'userId',
      headerName: 'User',
      width: 120,
      type: 'string',
    },
    {
      field: 'totalFee',
      headerName: 'Fee Amount',
      type: 'number',
      width: 170,
      valueGetter: (params) => {
        if (`${params.id}`.startsWith('auto-generated-row')) {
          if (tableRef.current && 'children' in params.rowNode) {
            const rows: UserFeeDeduction[] = (params.rowNode.children || []).map(tableRef.current.getApi().getRow);

            const amount = rows.reduce((res, ufd) => res + (ufd.totalFee || 0), 0);

            return `${round2Format(amount)}/${round2Format(rows?.[0]?.totalFee || 0)}`;
          }
        }

        return params.row?.totalFee;
      },
    },
    {
      field: 'userFee',
      headerName: 'User Fee',
      type: 'string',
      width: 300,
      valueGetter: (params) => {
        if (params.row?.id) {
          return `(${params.row?.userFee?.id})`;
        }
        return '';
      },
      groupingValueGetter: (params) => params.row?.userFee?.id,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return;
        }

        return (
          <Tooltip title={params.value} followCursor>
            <FakeButtonLink startIcon={<OpenInNewIcon />} href={locations.subscribedPortfolio(params.row?.userFee?.id)}>
              {params.value}
            </FakeButtonLink>
          </Tooltip>
        );
      },
    },
    {
      field: 'status.name',
      headerName: 'Status',
      type: 'singleSelect',
      valueOptions: Object.values(UserFeeDeductionStatus),
      minWidth: 200,
      flex: 1,
      aggregable: false,
      valueGetter: (params) => params.row?.status?.name,
      renderCell: (params: GridRenderCellParams) => {
        const info = userFeeDeductionStatusToInfo[params.row?.status?.name as UserFeeDeductionStatus] || {};
        const Icon = info?.icon;

        if (!Icon) {
          return params.row?.status?.name;
        }

        return <Chip variant="outlined" color={info?.color} icon={<Icon />} label={params.row?.status?.name} />;
      },
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      type: 'date',
      minWidth: 100,
      aggregable: false,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return '';
        }

        return params.formattedValue;
      },
    },
    {
      field: 'updatedAt',
      headerName: 'Updated',
      type: 'date',
      minWidth: 100,
      aggregable: false,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return '';
        }

        return params.formattedValue;
      },
    },
  ]);
  const { totalUserFeeDeductions, userFeeDeductions, fetchUserFeeDeductionsStatus } =
    useSelector(viewDetailsByBatchIdSelector);
  const { executeStatus, importExecutionFileStatus } = useSelector(feeBatchSelector);
  const loading =
    fetchUserFeeDeductionsStatus === fetchStatuses.pending ||
    importExecutionFileStatus === fetchStatuses.pending ||
    executeStatus === fetchStatuses.pending;

  useEffect(() => {
    if (importExecutionFileStatus === fetchStatuses.success || executeStatus === fetchStatuses.success) {
      tableRef.current?.reload();
    }
  }, [importExecutionFileStatus, executeStatus]);

  return (
    <>
      <Table
        ref={tableRef}
        fetchItems={fetchUserFeeDeductionsByBatchId}
        rows={userFeeDeductions}
        columns={columns}
        loading={loading}
        rowCount={totalUserFeeDeductions}
        disableRowGrouping={false}
        pageSizeOptions={[50, 100, 300]}
        initialPageSize={50}
        fetchItemsCustomParams={{ batchId }}
        rowGroupingColumnMode="single"
        initialRowGroupingModel={['userId']}
        initialColumnVisibilityModel={{ userId: false }}
        initialAggregationModel={{
          totalFee: 'sum',
        }}
        toolbarExportProps={{
          csvOptions: { fileName: `user-fee-deductions-${batchId}` },
          excelOptions: { fileName: `user-fee-deductions-${batchId}` },
        }}
      />

      {/* <AsyncDialog ref={asyncDialogRef} dialog={WithdrawAmountDialog} /> */}
    </>
  );
}
